<template>
  <div class="page-invites">
    <div class="page-invites__content">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-invites.new-invite')" @click="newInviteClick" />
      <div class="page-admin__table-header">
        <div class="page-invites__header-left">
          <div v-if="!isPhone" class="page-admin__count">
            <span class="number-monospace">{{ filteredInvites.length }}</span>
            {{ ' ' + $t('menu.admin-invites') }}
          </div>
          <FilterSingleChip
            is-text
            label-mode="collapse"
            icon="tag"
            :label="$t('search-filters.status.name')"
            :items="statusOptions"
            :value="statusFilter"
            @change="onStatusFilterChange"
          />
        </div>
        <div v-if="!isPhone" class="page-admin__actions">
          <BaseRefreshButton class="page-admin__refresh" :size="40" :pending="pending" @refresh="refreshInvites" />
          <BaseButton @click.native="newInviteClick" has-icon-left>
            <BaseIcon name="send" />
            {{ $t('page.admin-invites.new-invite') }}
          </BaseButton>
        </div>
      </div>
      <div class="page-invites__table">
        <TableNormal
          :headers="headers"
          :items="filteredInvites"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="pending"
          :loading-text="$t('page.admin-invites.loading')"
          :no-data-text="$t('page.admin-invites.no-invites')"
          hide-default-footer
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ filteredInvites.length }}</span>
            {{ ' ' + $t('menu.admin-invites') }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="refreshInvites" />
          </template>
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>
          <template v-slot:item.role="{ item }">
            <BaseTag v-if="item.role" tone="neutral" preset="soft">
              {{ $t('roles.' + item.role) }}
            </BaseTag>
            <span v-else>-</span>
          </template>
          <template v-slot:item.createdAt="{ item }">
            <span>
              {{ formatStandardDate(item.createdAt) }}
            </span>
          </template>
          <template v-slot:item.state="{ item }">
            <BaseTag preset="soft" :tone="getInviteStatusTone(item.state)">
              {{ $t('page.admin-invites.status.' + item.state) }}
            </BaseTag>
          </template>
          <template v-slot:item.expiresAt="{ item }">
            <span>
              {{ formatStandardDate(item.expiresAt) }}
            </span>
            <span v-if="item.daysBeforeExpire" :class="getColorClass(item.daysBeforeExpire)">
              ({{ getDhm(item.daysBeforeExpire) }} )
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <BaseMoreActions
              v-if="isPhone && rowActions(item).length"
              :title="item.email"
              :actions="rowActions(item)"
              @select="onActionsClick($event, item)"
            />
            <div v-else-if="!isPhone" class="tenders-data-table__actions">
              <BaseRoundButton
                :disabled="item.state === 'accepted'"
                :size="44"
                :tooltip-text="$t('page.admin-invites.actions.resend')"
                icon="send"
                is-white
                @click.native="onActionsClick('resend', item)"
              />
              <BaseRoundButton
                :size="44"
                :disabled="item.state !== 'pending'"
                :tooltip-text="$t('page.admin-invites.actions.revoke')"
                icon="trash-2"
                is-white
                @click.native="onActionsClick('revoke', item)"
              />
            </div>
          </template>
        </TableNormal>
      </div>
    </div>

    <!-- Phones list the invitations a page at a time as the reader scrolls (BasePhoneTable). -->
    <div v-if="pageCount > 1 && !isPhone" class="tenders-data-table__pagination">
      <TablePagination v-model="page" :length="pageCount" />
    </div>

    <ConfirmationDialog ref="confirmationDialog" :buttonText="buttonText" @execute="executeAction">
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="page.admin-invites.confirmation-dialog.content">
            <template v-slot:action>
              <span class="confirmation-dialog__action"> {{ buttonText }}</span>
            </template>
            <template v-slot:email>
              <span class="confirmation-dialog__user"> {{ action.user }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
    <AddInviteDialog ref="newInvitationDialog" @actionSucceeded="refreshInvites" />
  </div>
</template>

<script>
import AddInviteDialog from '@/components/page-admin-invites/AddInviteDialog';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import DateMixin from '@/mixins/date-mixin';
import TablePagination from '@/components/table/TablePagination.vue';

import { mapActions, mapGetters } from 'vuex';
import { getDhm, formatStandardDate } from '@/util/date-util';
import { getInviteStatusTone } from '@/util/tag-tones/invite-status-tag';
import FilterSingleChip from '@/components/filters/FilterSingleChip';
import TableNormal from '@/components/table/TableNormal.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';

// createdAt/expiresAt hold moment objects; Vuetify's default sort stringifies them
// (ordering by weekday name), so compare by timestamp instead. Non-dates ('-'/null) sort first.
const momentTimestamp = (value) => {
  const ms = value && typeof value.valueOf === 'function' ? value.valueOf() : NaN;
  return typeof ms === 'number' && !isNaN(ms) ? ms : -Infinity;
};
const compareDates = (a, b) => momentTimestamp(a) - momentTimestamp(b);

export default {
  name: 'PageAdminInvites',
  components: {
    PageMainAction,
    TableNormal,
    AddInviteDialog,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BaseMoreActions,
    BaseRefreshButton,
    BaseRoundButton,
    BaseTag,
    FilterSingleChip,
    ConfirmationDialog,
    TablePagination,
  },
  mixins: [DateMixin],
  data() {
    return {
      page: 1,
      sortBy: ['createdAt'],
      sortDesc: [true],
      itemsPerPage: 20,
      statusFilter: 'pending',
      headers: [
        { text: this.$t('login.id'), value: 'email' },
        { text: this.$t('page.admin-invites.role'), value: 'role', phone: 'meta' },
        { text: this.$t('search-filters.status.name'), value: 'state', phone: 'meta' },
        { text: this.$t('page.admin-invites.creation-date'), value: 'createdAt', sort: compareDates },
        { text: this.$t('page.admin-invites.expired-date'), value: 'expiresAt', sort: compareDates },
        { text: this.$t('page.tenders.data-table-titles.actions'), value: 'actions' },
      ],
      action: {
        type: 'default',
        user: '',
        inviteId: '',
      },
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('invites', ['invites', 'pending']),
    ...mapGetters('userProfile', ['userProfile']),
    statusOptions() {
      return [
        { text: this.$t('page.admin-invites.status.pending'), value: 'pending' },
        { text: this.$t('page.admin-invites.status.accepted'), value: 'accepted' },
        { text: this.$t('page.admin-invites.status.revoked'), value: 'revoked' },
      ];
    },
    filteredInvites() {
      // Empty filter (chip cleared) = show all.
      if (!this.statusFilter) return this.invites;
      return this.invites.filter((invite) => invite.state === this.statusFilter);
    },
    pageCount() {
      return Math.ceil(this.filteredInvites.length / this.itemsPerPage);
    },
    buttonText() {
      return this.action.type
        ? this.$t('page.admin-invites.actions.' + this.action.type)
        : this.$t('page.admin-invites.actions.default');
    },
  },
  methods: {
    ...mapActions('invites', ['getInvitesByTenant', 'resendInvite', 'revokeInvite']),
    ...mapActions('userProfile', ['getUserProfile']),
    formatStandardDate,
    getDhm,
    getInviteStatusTone,
    async refreshInvites() {
      await this.getInvitesByTenant(this.userProfile.tenantId);
    },
    newInviteClick() {
      this.$refs.newInvitationDialog.openDialog();
    },
    onStatusFilterChange(status) {
      // The chip's clear/toggle-off emits null; treat that as "no filter" (show all).
      this.statusFilter = status || '';
      this.page = 1;
    },
    // The row's ⋯ on phones, a sheet (BaseMoreActions): what the desktop's buttons allow, the
    // others left out (an accepted invitation has none).
    rowActions(invite) {
      return [
        ...(invite.state !== 'accepted'
          ? [{ key: 'resend', label: this.$t('page.admin-invites.actions.resend'), icon: 'send' }]
          : []),
        ...(invite.state === 'pending'
          ? [{ key: 'revoke', label: this.$t('page.admin-invites.actions.revoke'), icon: 'trash-2', danger: true }]
          : []),
      ];
    },
    onActionsClick(actionType, invite) {
      this.action.type = actionType;
      this.action.user = invite.email;
      this.action.inviteId = invite.id;
      this.$refs.confirmationDialog.openDialog();
    },
    executeAction() {
      if (this.action.type === 'resend') {
        this.resendInvite(this.action.inviteId)
          .then(() => {
            this.$toast.success(this.$t('page.admin-platforms.success'));
            this.refreshInvites();
            this.cancelAction();
          })
          .catch((error) => this.$toast.error(error));
      } else if (this.action.type === 'revoke') {
        this.revokeInvite(this.action.inviteId)
          .then(() => {
            this.$toast.success(this.$t('page.admin-platforms.success'));
            this.refreshInvites();
            this.cancelAction();
          })
          .catch((error) => this.$toast.error(error));
      }
    },
    cancelAction() {
      this.action = {
        type: '',
        user: '',
        inviteId: '',
      };
    },
  },
  async created() {
    await this.getUserProfile();
    await this.getInvitesByTenant(this.userProfile.tenantId);
  },
};
</script>

<style lang="scss">
.page-invites {
  &__header {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    justify-content: space-between;
    display: flex;
    align-items: center;
    min-height: $header-height;
    position: sticky;
    top: $menu-top-bar-height;
    z-index: $header-index;
    padding: 0px 32px;
  }

  &__head {
    display: flex;
    align-items: center;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .page-admin__count {
      margin-bottom: 0;
    }
  }

  &__add-invite {
    &--icon {
      height: 24px;
      padding-left: 8px;
    }
  }

  &__content {
    padding: 32px;

    .v-data-table.theme--light tbody {
      cursor: default;
    }

    .v-data-table.theme--light tbody td {
      color: $color-normal;
    }
  }

  .v-data-table__progress {
    .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }

  @media screen and (max-width: 1080px) {
    &__title {
      font-size: 25px;
    }
  }
}
</style>
